import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  RefreshCw,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  User as UserIcon,
  Mail,
  Phone,
  Calendar,
  AlertCircle,
  X
} from 'lucide-react';
import { User } from '../types';
import apiService from '../services/api';
import { formatDateTime } from '../utils/timeUtils';

const EvaluatorsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [evaluators, setEvaluators] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvaluator, setEditingEvaluator] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
  });

  const fetchEvaluators = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getUsers('evaluator');
      setEvaluators(data);
      setError('');
    } catch (err) {
      setError(t('evaluators.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchEvaluators();
  }, [fetchEvaluators]);

  const handleOpenDialog = (evaluator?: User) => {
    if (evaluator) {
      setEditingEvaluator(evaluator);
      setFormData({
        firstName: evaluator.firstName,
        lastName: evaluator.lastName,
        email: evaluator.email,
        password: '',
        phone: evaluator.phone || '',
      });
    } else {
      setEditingEvaluator(null);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        phone: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingEvaluator(null);
  };

  const handleSave = async () => {
    try {
      const payload: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        userType: 'evaluator' as const,
      };

      // Only include password if it's not empty
      if (formData.password && formData.password.trim() !== '') {
        payload.password = formData.password;
      }

      if (editingEvaluator) {
        await apiService.updateUser(editingEvaluator.id, payload);
      } else {
        await apiService.createUser(payload);
      }
      await fetchEvaluators();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.response?.data?.message || t('evaluators.saveFailed'));
    }
  };

  const handleToggleActivation = async (id: number, activate: boolean) => {
    try {
      if (activate) {
        await apiService.activateUser(id);
      } else {
        await apiService.deactivateUser(id);
      }
      await fetchEvaluators();
    } catch (err) {
      setError(activate ? t('evaluators.activateFailed') : t('evaluators.deactivateFailed'));
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('evaluators.confirmDelete'))) return;

    try {
      await apiService.deleteUser(id);
      await fetchEvaluators();
    } catch (err) {
      setError(t('evaluators.deleteFailed'));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-50/30 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Premium Header */}
        <div className="relative overflow-hidden rounded-3xl mb-8 shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-r from-brand-600 via-brand-700 to-emerald-700 opacity-95"></div>
          <div className="absolute inset-0 backdrop-blur-3xl bg-white/10"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -mr-48 -mt-48"></div>
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-brand-800/20 rounded-full -ml-36 -mb-36"></div>

          <div className="relative px-8 py-10">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-black text-white mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                  {t('evaluators.title')}
                </h1>
                <p className="text-brand-50 text-lg font-medium" dir={isRTL ? 'rtl' : 'ltr'}>
                  {isRTL ? 'إدارة المفتشين' : 'Manage inspectors'}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={fetchEvaluators}
                  disabled={loading}
                  className="p-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-2xl text-white transition-all duration-300 hover:scale-105 disabled:opacity-50"
                >
                  <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={() => handleOpenDialog()}
                  className="flex items-center gap-2 px-6 py-3 bg-white hover:bg-brand-50 text-brand-700 font-bold rounded-2xl transition-all duration-300 hover:scale-105 shadow-lg"
                >
                  <Plus className="w-5 h-5" />
                  <span>{t('evaluators.new')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 rounded-2xl p-4 flex items-start gap-3 animate-fadeIn">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-800 font-medium">{error}</p>
            </div>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && evaluators.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-16 h-16 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin mb-4"></div>
            <p className="text-gray-500 font-medium">{isRTL ? 'جاري التحميل...' : 'Loading...'}</p>
          </div>
        ) : (
          <>
            {/* Evaluators Table */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-brand-600 via-brand-700 to-emerald-700">
                      <th className="px-6 py-4 text-left text-sm font-black text-white uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>
                        {t('evaluators.firstName')}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-black text-white uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>
                        {t('evaluators.lastName')}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-black text-white uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>
                        {t('evaluators.email')}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-black text-white uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>
                        {t('evaluators.phone')}
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-black text-white uppercase tracking-wide">
                        {t('evaluators.status')}
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-black text-white uppercase tracking-wide">
                        {t('evaluators.dateJoined')}
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-black text-white uppercase tracking-wide">
                        {t('common.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {evaluators.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center">
                            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                              <UserIcon className="w-10 h-10 text-gray-400" />
                            </div>
                            <p className="text-gray-500 font-medium">
                              {isRTL ? 'لا يوجد مفتشون' : 'No inspectors found'}
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      evaluators.map((evaluator, index) => (
                        <tr
                          key={evaluator.id}
                          className="hover:bg-brand-50/30 transition-colors"
                          style={{ animationDelay: `${index * 0.05}s` }}
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-brand-100 rounded-full flex items-center justify-center">
                                <span className="text-brand-700 font-bold text-sm">
                                  {evaluator.firstName.charAt(0)}
                                </span>
                              </div>
                              <span className="font-semibold text-gray-800">{evaluator.firstName}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-gray-700 font-medium">{evaluator.lastName}</td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2 text-gray-600">
                              <Mail className="w-4 h-4 text-brand-600" />
                              <span className="text-sm">{evaluator.email}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2 text-gray-600">
                              <Phone className="w-4 h-4 text-brand-600" />
                              <span className="text-sm">{evaluator.phone || '-'}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border ${
                              evaluator.isActive
                                ? 'bg-green-100 text-green-800 border-green-200'
                                : 'bg-gray-100 text-gray-800 border-gray-200'
                            }`}>
                              {evaluator.isActive ? (
                                <>
                                  <CheckCircle className="w-3 h-3" />
                                  {t('evaluators.active')}
                                </>
                              ) : (
                                <>
                                  <XCircle className="w-3 h-3" />
                                  {t('evaluators.inactive')}
                                </>
                              )}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-center gap-2 text-gray-600">
                              <Calendar className="w-4 h-4 text-brand-600" />
                              <span className="text-sm">{evaluator.createdAt ? formatDateTime(evaluator.createdAt) : '-'}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => handleOpenDialog(evaluator)}
                                className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-all"
                                title={t('common.edit')}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              {evaluator.isActive ? (
                                <button
                                  onClick={() => handleToggleActivation(evaluator.id, false)}
                                  className="p-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-all"
                                  title={t('evaluators.deactivate')}
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleToggleActivation(evaluator.id, true)}
                                  className="p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-all"
                                  title={t('evaluators.activate')}
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => handleDelete(evaluator.id)}
                                className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-all"
                                title={t('common.delete')}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Add/Edit Dialog */}
        {dialogOpen && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
            <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scaleIn">
              {/* Dialog Header */}
              <div className="sticky top-0 bg-gradient-to-r from-brand-600 to-brand-700 px-8 py-6 rounded-t-3xl z-10">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-white" dir={isRTL ? 'rtl' : 'ltr'}>
                    {editingEvaluator ? t('evaluators.editEvaluator') : t('evaluators.addEvaluator')}
                  </h2>
                  <button
                    onClick={handleCloseDialog}
                    className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all"
                  >
                    <X className="w-6 h-6 text-white" />
                  </button>
                </div>
              </div>

              {/* Dialog Body */}
              <div className="p-8 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                      {t('evaluators.firstName')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      required
                      dir={isRTL ? 'rtl' : 'ltr'}
                      className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:ring-4 focus:ring-brand-100 focus:bg-white transition-all outline-none font-medium"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                      {t('evaluators.lastName')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      required
                      dir={isRTL ? 'rtl' : 'ltr'}
                      className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:ring-4 focus:ring-brand-100 focus:bg-white transition-all outline-none font-medium"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('evaluators.email')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    dir={isRTL ? 'rtl' : 'ltr'}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:ring-4 focus:ring-brand-100 focus:bg-white transition-all outline-none font-medium"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('evaluators.password')} {!editingEvaluator && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingEvaluator}
                    dir={isRTL ? 'rtl' : 'ltr'}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:ring-4 focus:ring-brand-100 focus:bg-white transition-all outline-none font-medium"
                  />
                  {editingEvaluator && (
                    <p className="text-xs text-gray-500 mt-1" dir={isRTL ? 'rtl' : 'ltr'}>
                      {t('evaluators.passwordHelperText')}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('evaluators.phone')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    dir={isRTL ? 'rtl' : 'ltr'}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:ring-4 focus:ring-brand-100 focus:bg-white transition-all outline-none font-medium"
                  />
                </div>
              </div>

              {/* Dialog Footer */}
              <div className="sticky bottom-0 bg-gray-50 px-8 py-6 rounded-b-3xl border-t border-gray-200 flex gap-4">
                <button
                  onClick={handleCloseDialog}
                  className="flex-1 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all font-bold"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSave}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-700 hover:to-brand-800 text-white font-bold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EvaluatorsPage;
